import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Message,
  Table,
  Dropdown,
  Header,
  Progress,
  Label,
  Icon,
  Segment,
  Grid,
} from "semantic-ui-react";
import * as XLSX from "xlsx";
import { documentApi } from "@api/documents";
import ExcelTemplate from "./ExcelTemplate";

const CALL_NUMBER_SUFFIXES = ["a", "b", "c", "d", "e"];

// Define all possible document fields for mapping
const DOCUMENT_FIELDS = [
  { key: "title", label: "Title (Required)", required: true },
  { key: "call_number", label: "Call Number", required: false },
  { key: "authors", label: "Author(s)", required: false },
  { key: "isbn", label: "ISBN", required: false },
  { key: "publisher", label: "Publisher", required: false },
  { key: "published_year", label: "Published Year", required: false },
  { key: "language", label: "Language", required: false },
  { key: "subjects", label: "Subjects/Genre", required: false },
  { key: "document_type", label: "Document Type", required: false },
  { key: "edition", label: "Edition", required: false },
  { key: "pages", label: "Number of Pages", required: false },
  { key: "abstract", label: "Abstract/Description", required: false },
  { key: "notes", label: "Notes/Additional Info", required: false },
  { key: "ignore", label: "-- Ignore this column --", required: false },
];

class ExcelImportUploader extends Component {
  static propTypes = {
    trigger: PropTypes.element,
    onImportComplete: PropTypes.func,
  };

  static defaultProps = {
    trigger: null,
    onImportComplete: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      showImportModal: false,
      importFile: null,
      importData: [],
      rawExcelData: [],
      excelHeaders: [],
      columnMapping: {},
      importProgress: 0,
      isImporting: false,
      importResults: {
        successful: [],
        failed: [],
        duplicates: [],
      },
      showResultsModal: false,
      previewData: [],
      currentStep: "upload",
      subjectScheme: "LOC",
      availableSubjectSchemes: [
        { key: "LOC", text: "LOC (Library of Congress)", value: "LOC" },
        {
          key: "UDC",
          text: "UDC (Universal Decimal Classification)",
          value: "UDC",
        },
        { key: "DEWEY", text: "Dewey Decimal Classification", value: "DEWEY" },
        {
          key: "ICS",
          text: "ICS (International Classification Standards)",
          value: "ICS",
        },
      ],
    };
    this.fileInputRef = React.createRef();
  }

  handleImportClick = () => {
    this.setState({
      showImportModal: true,
      currentStep: "upload",
    });
  };

  handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      this.setState({ importFile: file });
      this.parseExcelFile(file);
    }
  };

  parseExcelFile = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length < 2) {
        throw new Error(
          "Excel file must contain at least a header row and one data row"
        );
      }

      const headers = jsonData[0].map((h) => String(h).trim());
      const dataRows = jsonData
        .slice(1)
        .filter((row) =>
          row.some((cell) => cell !== undefined && cell !== null && cell !== "")
        );

      const autoMapping = this.autoDetectMapping(headers);

      this.setState({
        rawExcelData: dataRows,
        excelHeaders: headers,
        columnMapping: autoMapping,
        currentStep: "mapping",
      });
    } catch (error) {
      alert(`Error parsing Excel file: ${error.message}`);
    }
  };

  autoDetectMapping = (headers) => {
    const mapping = {};

    headers.forEach((header, index) => {
      const normalizedHeader = header.toLowerCase().trim();

      if (
        normalizedHeader.includes("title") ||
        normalizedHeader.includes("book")
      ) {
        mapping[index] = "title";
      } else if (normalizedHeader.includes("author")) {
        mapping[index] = "authors";
      } else if (
        normalizedHeader.includes("call") &&
        normalizedHeader.includes("number")
      ) {
        mapping[index] = "call_number";
      } else if (normalizedHeader.includes("isbn")) {
        mapping[index] = "isbn";
      } else if (normalizedHeader.includes("publisher")) {
        mapping[index] = "publisher";
      } else if (
        normalizedHeader.includes("year") ||
        normalizedHeader.includes("published") ||
        normalizedHeader.includes("publication")
      ) {
        mapping[index] = "published_year";
      } else if (normalizedHeader.includes("language")) {
        mapping[index] = "language";
      } else if (
        normalizedHeader.includes("subject") ||
        normalizedHeader.includes("genre") ||
        normalizedHeader.includes("topic")
      ) {
        mapping[index] = "subjects";
      } else if (
        normalizedHeader.includes("type") ||
        normalizedHeader.includes("material")
      ) {
        mapping[index] = "document_type";
      } else if (normalizedHeader.includes("edition")) {
        mapping[index] = "edition";
      } else if (normalizedHeader.includes("page")) {
        mapping[index] = "pages";
      } else if (
        normalizedHeader.includes("abstract") ||
        normalizedHeader.includes("description") ||
        normalizedHeader.includes("summary")
      ) {
        mapping[index] = "abstract";
      } else if (
        normalizedHeader.includes("sales") ||
        normalizedHeader.includes("note") ||
        normalizedHeader.includes("comment")
      ) {
        mapping[index] = "notes";
      } else {
        mapping[index] = "ignore";
      }
    });

    return mapping;
  };

  handleMappingChange = (columnIndex, fieldKey) => {
    this.setState((prevState) => ({
      columnMapping: {
        ...prevState.columnMapping,
        [columnIndex]: fieldKey,
      },
    }));
  };

  processWithMapping = () => {
    const { rawExcelData, excelHeaders, columnMapping } = this.state;
    const defaultSubjectScheme = "LOC";

    const hasTitleMapping = Object.values(columnMapping).includes("title");
    if (!hasTitleMapping) {
      alert('Please map at least one column to "Title" as it is required.');
      return;
    }

    const mappedData = rawExcelData.map((row, index) => {
      const document = {};

      excelHeaders.forEach((header, colIndex) => {
        const fieldKey = columnMapping[colIndex];
        const cellValue = row[colIndex];

        if (
          fieldKey &&
          fieldKey !== "ignore" &&
          cellValue !== undefined &&
          cellValue !== null &&
          cellValue !== ""
        ) {
          const cleanValue = String(cellValue).trim();

          switch (fieldKey) {
            case "title":
              document.title = cleanValue;
              break;
            case "call_number":
              document.call_number = cleanValue;
              break;
            case "authors":
              document.authors = cleanValue.split(/[,;]/).map((author) => ({
                full_name: author.trim(),
                type: "PERSON",
              }));
              break;
            case "isbn":
              document.identifiers = [{ scheme: "ISBN", value: cleanValue }];
              break;
            case "publisher":
              if (!document.imprint) document.imprint = {};
              document.imprint.publisher = cleanValue;
              break;
            case "published_year": {
              document.publication_year = String(cleanValue).trim();
              if (!document.imprint) document.imprint = {};
              document.imprint.date = String(cleanValue).trim();
              break;
            }
            case "language": {
              let languageCode = "eng";
              const normalized = cleanValue.toLowerCase().trim();

              if (normalized === "spanish" || normalized === "español") {
                languageCode = "spa";
              } else if (normalized === "french" || normalized === "français") {
                languageCode = "fra";
              } else if (
                normalized === "tagalog" ||
                normalized === "filipino"
              ) {
                languageCode = "tgl";
              }

              document.languages = [languageCode];
              break;
            }
            case "subjects":
              document.subjects = cleanValue.split(/[,;]/).map((subject) => ({
                scheme: defaultSubjectScheme,
                value: subject.trim(),
              }));
              break;
            case "document_type": {
              let docType = cleanValue.toUpperCase();

              const typeMapping = {
                PERIODICAL: "SERIAL_ISSUE",
                JOURNAL: "SERIAL_ISSUE",
                MAGAZINE: "SERIAL_ISSUE",
                VIDEO: "MULTIMEDIA",
                AUDIO: "MULTIMEDIA",
              };

              document.document_type = typeMapping[docType] || docType;
              break;
            }
            case "edition":
              document.edition = cleanValue;
              break;
            case "pages":
              document.number_of_pages = String(cleanValue).trim();
              break;
            case "abstract":
              document.abstract = cleanValue;
              break;
            case "notes":
              document.note = cleanValue;
              break;
          }
        }
      });

      // Generate call number if missing
      if (!document.call_number && document.title) {
        const year =
          document.publication_year || document.imprint?.date || "0000";
        const titleCode = document.title
          .split(" ")
          .slice(0, 2)
          .join("")
          .replace(/[^A-Za-z]/g, "")
          .substring(0, 4)
          .toUpperCase();
        document.call_number = `800 ${titleCode} ${year}`;
      }

      if (!document.document_type) {
        document.document_type = "BOOK";
      }

      if (!document.publication_year) {
        document.publication_year = String(new Date().getFullYear());
      }

      if (!document.subjects || document.subjects.length === 0) {
        document.subjects = [
          { scheme: defaultSubjectScheme, value: "General" },
        ];
      }

      document._rowIndex = index + 2;
      return document;
    });

    const validData = mappedData.filter((doc) => doc.title);

    this.setState({
      importData: validData,
      previewData: validData.slice(0, 10),
      currentStep: "preview",
    });
  };

  validateDocument = (document) => {
    const errors = [];

    if (!document.title) {
      errors.push("Title is required");
    }
    return errors;
  };

  checkCallNumberUniqueness = async (callNumber) => {
    try {
      const response = await documentApi.list(`call_number:"${callNumber}"`, {
        size: 10,
      });
      const existingDocuments = response.data.hits;
      return {
        isUnique: existingDocuments.length === 0,
        existingDocuments,
        suggestedCallNumber:
          existingDocuments.length > 0
            ? this.generateUniqueCallNumber(
                callNumber,
                existingDocuments.length
              )
            : null,
      };
    } catch (error) {
      return {
        isUnique: true,
        existingDocuments: [],
        suggestedCallNumber: null,
      };
    }
  };

  generateUniqueCallNumber = (baseCallNumber, conflictCount) => {
    return conflictCount <= CALL_NUMBER_SUFFIXES.length
      ? `${baseCallNumber}${CALL_NUMBER_SUFFIXES[conflictCount - 1]}`
      : `${baseCallNumber}-${conflictCount + 1}`;
  };

  startImport = async () => {
    const { importData } = this.state;

    this.setState({
      isImporting: true,
      importProgress: 0,
      importResults: { successful: [], failed: [], duplicates: [] },
      currentStep: "import",
    });

    const results = { successful: [], failed: [], duplicates: [] };
    const total = importData.length;

    for (let i = 0; i < importData.length; i++) {
      const document = importData[i];
      const progress = Math.round(((i + 1) / total) * 100);

      this.setState({ importProgress: progress });

      try {
        const validationErrors = this.validateDocument(document);
        if (validationErrors.length > 0) {
          results.failed.push({
            document,
            error: validationErrors.join(", "),
            rowIndex: document._rowIndex,
          });
          continue;
        }

        const uniquenessCheck = await this.checkCallNumberUniqueness(
          document.call_number
        );

        if (!uniquenessCheck.isUnique) {
          document.call_number = uniquenessCheck.suggestedCallNumber;
          results.duplicates.push({
            document,
            originalCallNumber: importData[i].call_number,
            newCallNumber: document.call_number,
            rowIndex: document._rowIndex,
          });
        }

        const cleanDocument = { ...document };
        delete cleanDocument._rowIndex;

        const response = await documentApi.create(cleanDocument);
        results.successful.push({
          document: cleanDocument,
          response: response.data,
          rowIndex: document._rowIndex,
        });
      } catch (error) {
        results.failed.push({
          document,
          error: error.message || "Unknown error occurred",
          rowIndex: document._rowIndex,
        });
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    this.setState({
      isImporting: false,
      importResults: results,
      showImportModal: false,
      showResultsModal: true,
      importProgress: 100,
    });

    // Call onImportComplete callback if provided
    if (this.props.onImportComplete) {
      this.props.onImportComplete(results);
    }
  };

  resetImport = () => {
    this.setState({
      showImportModal: false,
      importFile: null,
      importData: [],
      rawExcelData: [],
      excelHeaders: [],
      columnMapping: {},
      importProgress: 0,
      isImporting: false,
      importResults: { successful: [], failed: [], duplicates: [] },
      showResultsModal: false,
      previewData: [],
      currentStep: "upload",
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  goBackToMapping = () => {
    this.setState({ currentStep: "mapping" });
  };

  renderUploadStep = () => {
    const { importFile } = this.state;

    return (
      <div>
        <Message info>
          <Message.Header>Flexible Excel Import</Message.Header>
          <p>
            Upload any Excel file (.xlsx) and map your columns to document
            fields. The system will:
          </p>
          <ul>
            <li>Auto-detect common column names</li>
            <li>Allow you to manually map any column to any document field</li>
            <li>Generate call numbers automatically if not provided</li>
            <li>Handle various formats and structures</li>
          </ul>
        </Message>

        <div style={{ textAlign: "center", margin: "2rem 0" }}>
          <ExcelTemplate
            trigger={
              <Button
                primary
                icon="download"
                content="Download Sample Template"
                style={{ marginBottom: "1rem" }}
              />
            }
          />
          <br />
          <input
            ref={this.fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={this.handleFileSelect}
            style={{ display: "none" }}
          />
          <Button
            secondary
            icon="file excel"
            content="Select Excel File"
            onClick={() => this.fileInputRef.current?.click()}
          />
          {importFile && (
            <Message positive style={{ marginTop: "1rem" }}>
              File selected: {importFile.name}
            </Message>
          )}
        </div>
      </div>
    );
  };

  renderMappingStep = () => {
    const {
      excelHeaders,
      columnMapping,
      rawExcelData,
      subjectScheme,
      availableSubjectSchemes,
    } = this.state;

    const fieldOptions = DOCUMENT_FIELDS.map((field) => ({
      key: field.key,
      value: field.key,
      text: field.label,
    }));

    return (
      <div>
        <Message info>
          <Message.Header>Map Your Columns</Message.Header>
          <p>
            Map each Excel column to a document field. Auto-detection has been
            applied, but you can change any mapping.
          </p>
        </Message>

        <Segment>
          <Header as="h4">Subject Classification Scheme</Header>
          <p>Choose which classification scheme to use for subjects/topics:</p>
          <Dropdown
            fluid
            selection
            options={availableSubjectSchemes}
            value={subjectScheme}
            onChange={(e, { value }) => this.setState({ subjectScheme: value })}
            placeholder="Select subject scheme"
          />
        </Segment>

        <Segment>
          <Header as="h4">Column Mapping</Header>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Excel Column</Table.HeaderCell>
                <Table.HeaderCell>Sample Data</Table.HeaderCell>
                <Table.HeaderCell>Maps to Document Field</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {excelHeaders.map((header, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <strong>{header}</strong>
                  </Table.Cell>
                  <Table.Cell>
                    {rawExcelData[0] && rawExcelData[0][index] ? (
                      String(rawExcelData[0][index]).substring(0, 50) +
                      (String(rawExcelData[0][index]).length > 50 ? "..." : "")
                    ) : (
                      <em>Empty</em>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Dropdown
                      fluid
                      selection
                      options={fieldOptions}
                      value={columnMapping[index] || "ignore"}
                      onChange={(e, { value }) =>
                        this.handleMappingChange(index, value)
                      }
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      </div>
    );
  };

  renderPreviewStep = () => {
    const { previewData, importData } = this.state;

    return (
      <div>
        <Message positive>
          <Message.Header>Ready to Import</Message.Header>
          <p>
            Found {importData.length} valid documents. Preview of first 10
            records:
          </p>
        </Message>

        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Title</Table.HeaderCell>
              <Table.HeaderCell>Author</Table.HeaderCell>
              <Table.HeaderCell>Year</Table.HeaderCell>
              <Table.HeaderCell>Language</Table.HeaderCell>
              <Table.HeaderCell>Subjects</Table.HeaderCell>
              <Table.HeaderCell>Call Number</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {previewData.map((doc, index) => (
              <Table.Row key={index}>
                <Table.Cell>{doc.title || <em>Missing</em>}</Table.Cell>
                <Table.Cell>
                  {doc.authors?.map((a) => a.full_name).join(", ") || (
                    <em>No author</em>
                  )}
                </Table.Cell>
                <Table.Cell>{doc.imprint?.date || <em>No year</em>}</Table.Cell>
                <Table.Cell>
                  {doc.languages?.[0] || <em>No language</em>}
                </Table.Cell>
                <Table.Cell>
                  {doc.subjects?.map((s) => s.subject).join(", ") || (
                    <em>No subjects</em>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Label size="small" color="green">
                    {doc.call_number}
                  </Label>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  };

  renderImportStep = () => {
    const { importProgress } = this.state;

    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <Header as="h3">Importing Documents...</Header>
        <Progress percent={importProgress} indicating size="large">
          {importProgress}% Complete
        </Progress>
        <p>Please wait while we process your documents...</p>
      </div>
    );
  };

  render() {
    const {
      showImportModal,
      importData,
      isImporting,
      importResults,
      showResultsModal,
      currentStep,
    } = this.state;

    const { trigger } = this.props;

    return (
      <>
        {/* Trigger button */}
        {trigger ? (
          React.cloneElement(trigger, { onClick: this.handleImportClick })
        ) : (
          <Button
            fluid
            color="green"
            icon="upload"
            content="Import Excel"
            onClick={this.handleImportClick}
          />
        )}

        {/* Import Modal */}
        <Modal open={showImportModal} onClose={this.resetImport} size="large">
          <Modal.Header>
            <Icon name="upload" /> Import Documents from Excel
          </Modal.Header>
          <Modal.Content scrolling>
            {currentStep === "upload" && this.renderUploadStep()}
            {currentStep === "mapping" && this.renderMappingStep()}
            {currentStep === "preview" && this.renderPreviewStep()}
            {currentStep === "import" && this.renderImportStep()}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.resetImport} disabled={isImporting}>
              Cancel
            </Button>
            {currentStep === "mapping" && (
              <Button
                primary
                onClick={this.processWithMapping}
                icon="arrow right"
                content="Continue to Preview"
              />
            )}
            {currentStep === "preview" && (
              <>
                <Button
                  onClick={this.goBackToMapping}
                  icon="arrow left"
                  content="Back to Mapping"
                />
                <Button
                  primary
                  onClick={this.startImport}
                  disabled={isImporting || importData.length === 0}
                  loading={isImporting}
                  icon="upload"
                  content={`Import ${importData.length} Documents`}
                />
              </>
            )}
          </Modal.Actions>
        </Modal>

        {/* Results Modal */}
        <Modal open={showResultsModal} onClose={this.resetImport} size="large">
          <Modal.Header>
            <Icon name="checkmark" /> Import Results
          </Modal.Header>
          <Modal.Content scrolling>
            <Grid columns={3} divided>
              <Grid.Column>
                <Header as="h4" color="green">
                  <Icon name="check circle" />
                  Successful ({importResults.successful.length})
                </Header>
                {importResults.successful.map((item, index) => (
                  <Label key={index} color="green" style={{ margin: "2px" }}>
                    Row {item.rowIndex}: {item.document.title}
                  </Label>
                ))}
              </Grid.Column>

              <Grid.Column>
                <Header as="h4" color="orange">
                  <Icon name="warning circle" />
                  Duplicates Resolved ({importResults.duplicates.length})
                </Header>
                {importResults.duplicates.map((item, index) => (
                  <div key={index} style={{ marginBottom: "1rem" }}>
                    <Label color="orange">
                      Row {item.rowIndex}: {item.document.title}
                    </Label>
                    <div style={{ fontSize: "0.8em", color: "#666" }}>
                      Changed: {item.originalCallNumber} → {item.newCallNumber}
                    </div>
                  </div>
                ))}
              </Grid.Column>

              <Grid.Column>
                <Header as="h4" color="red">
                  <Icon name="times circle" />
                  Failed ({importResults.failed.length})
                </Header>
                {importResults.failed.map((item, index) => (
                  <div key={index} style={{ marginBottom: "1rem" }}>
                    <Label color="red">
                      Row {item.rowIndex}: {item.document.title || "No title"}
                    </Label>
                    <div style={{ fontSize: "0.8em", color: "#666" }}>
                      Error: {item.error}
                    </div>
                  </div>
                ))}
              </Grid.Column>
            </Grid>
          </Modal.Content>
          <Modal.Actions>
            <Button primary onClick={this.resetImport}>
              Close
            </Button>
          </Modal.Actions>
        </Modal>
      </>
    );
  }
}

export default ExcelImportUploader;
